import { useState, type ReactNode } from 'react';
import {
  ACCESS_ACTIONS,
  ACCESS_ACTION_LABEL,
  ACCESS_WORD,
  CONFIRM_LABEL,
  accessSubLine,
  confirmMessage,
  deadLinkReason,
  inviteDraftMessage,
  isCopyable,
  isRegistered,
  joinPath,
  subLineInstant,
  type AccessAction,
  type AccessPerson,
} from '@bbbk/schema';
import { Avatar, Icon, Skeleton, DisclosureRegion } from '../common';
import { PeopleSkeleton } from '../bookings/RequestCard';
import { AccessGlyph } from './AccessGlyph';
import { ManageIcon, ReissueIcon, RevokeIcon } from './AccessIcons';
import { MessageSlot } from '../bookings/MessageSlot';
import { accessDateLabel, accessDayLabel } from './accessDate';

// G16 · App access — the household modal's third column.
//
// ONE ROW TEMPLATE FOR ALL SIX STATES (AC7). *Copy message* and the link sit in
// the same place in every one of them; only the line under the name and the undo
// beneath the message change. That is not a styling preference — it is what lets
// Patricia scan a household without re-reading the layout each time, and it is
// why this component takes a state and prunes, rather than branching into six
// shapes.
//
// The words, the actions and the draft all come from `@bbbk/schema`: the API
// derives the same states from the same rules, and a second opinion about what
// *Signing up* offers would be a bug nobody could see from either side alone.

/** Which row's Manage drawer is open, and what it is asking about. */
export interface AccessUiState {
  /** The row expanded to its draft — one at a time, like the prototype. */
  openId: number | null;
  /** The row whose Manage access strip is open beneath the draft. */
  manageId: number | null;
  /** The destructive action awaiting its inline yes. */
  confirm: { contactId: number; action: AccessAction } | null;
  /** The action in flight, and for whom — see the pending draft below. */
  pending: { contactId: number; action: AccessAction } | null;
}

export interface AccessColumnProps extends AccessUiState {
  /** `null` while the household's people load: the column keeps its shape and the fold is busy, so nothing above it remounts when they land (2026-10-11). */
  people: readonly AccessPerson[] | null;
  onToggle: (contactId: number) => void;
  onManage: (contactId: number) => void;
  onConfirm: (confirm: { contactId: number; action: AccessAction } | null) => void;
  onAction: (person: AccessPerson, action: AccessAction) => void;
  /** The contact a write is in flight for, so its controls can rest. */
  busy: number | null;
  /** Where a link points. The browser's own origin in the app; injectable for tests. */
  origin: string;
  /** The message beside a person's link — the household's next step (messages-one-shape); today's invite draft when none is given. */
  draft?: (person: AccessPerson, url: string, registered: boolean) => string;
  /** A card above App access in the same column — the request card on a booking (accept-or-decline). */
  lead?: ReactNode;
  /** The lead carries the column's heading, so the column drops its own (Freddi, 2026-10-10). */
  leadHeads?: boolean;
  /** Open when the column first draws — closed by default; the row tests draw it open. */
  startOpen?: boolean;
  /** A container App access lives INSIDE — the request card (Freddi, 2026-10-10: "app access card should
   *  live within self service container"). The fold is handed in, inset; the column draws nothing else. */
  /** The container the household rides in (the request card): it is handed the rows themselves — no fold, no head of its own (2026-10-11). */
  frame?: (rows: ReactNode) => ReactNode;
}

/* THE APP ACCESS FOLD'S HEAD (Freddi, 2026-10-10: "change the card with users
   access from their name to app access and within that it should show both
   users"). The tag is the household's state in one word or a count; the
   glance names everybody and how many of them can sign in, so the people are
   on screen even while the fold is shut. */
export function accessFoldTag(people: readonly AccessPerson[]): { word: string; tone: '' | 'ok' | 'todo' } {
  const signedIn = people.filter((p) => isRegistered(p.state)).length;
  if (people.length > 0 && signedIn === people.length) return { word: 'Registered', tone: 'ok' };
  if (signedIn === 0) return { word: 'Not set up', tone: '' };
  return { word: `${signedIn} of ${people.length} registered`, tone: 'todo' };
}

export function accessFoldGlance(people: readonly AccessPerson[]): string {
  const signedIn = people.filter((p) => isRegistered(p.state)).length;
  // THE COUNT FIRST: two long names would otherwise push it past the ellipsis.
  return `${signedIn} of ${people.length} can sign in · ${people.map((p) => p.name).join(', ')}`;
}

// EVERY PIECE OF ROW STATE LIVES ABOVE THE ROW, deliberately. Which row is
// open, which drawer is showing and which confirmation is pending are all one
// question — "what is Patricia in the middle of?" — and a row holding its own
// answer means two rows can each believe they are the one being acted on. It
// also makes every state of this column renderable from props alone, which is
// what lets the visual contract assert the drawn screens rather than a
// hand-copy of them.

const NOTE =
  'Each person gets their own link. Open one for the draft to paste into WhatsApp; ' +
  'everything that ends a link sits under Manage access and asks before it acts.';




/**
 * THE DRAFT AS IT WILL BE, for a person with no link yet (Freddi, 2026-09-14).
 *
 * *Not registered* used to open to a one-line box, and *Link generated* to a
 * three-field draft — so pressing New link grew the row by a hundred pixels
 * under Patricia's eye. The space is filled with what is already known rather
 * than with padding: the number (real, copyable — she may text first and link
 * later), the message with a placeholder where the link will go, and the link
 * slot saying what fills it. Three fields, the same three, the same height: New
 * link changes the contents of a shape that is already there.
 */
function DraftPreview({ person, busy, onGenerate }: { person: AccessPerson; busy: boolean; onGenerate: () => void }) {
  return (
    <div className="ac-msg ac-draft is-preview">
      {/* NO WHATSAPP UNTIL THERE IS A LINK (Freddi, 2026-10-14: "the open whatsapp button shouldnt
          appear until the link is generated"). Generate link is the one thing here. */}
      {/* WHERE THE MESSAGE AND THE LINK WILL BE, the one thing that makes them —
          centred, with room around it (Freddi, 2026-09-14). The panel is the
          height the two fields will take, so pressing it changes the contents of
          a shape that is already there rather than growing the card. */}
      <div className="ac-f ac-generate">
        <button type="button" className="ac-btn ac-generate-btn" disabled={busy} onClick={onGenerate}>
          <ReissueIcon size={14} />
          Generate link
        </button>
        <p className="ac-generate-note">Makes the message and the link to send. Works for 24 hours.</p>
      </div>
    </div>
  );
}

/**
 * THE SLOT'S OWN ROW, as a bar, while the link is made: the message slot is one
 * row whatever it holds, so the pending row is that row and nothing moves when
 * the words land.
 */
function PendingDraft() {
  return (
    <div className="ac-msg ac-draft is-pending" aria-busy="true">
      <div className="ss-msg is-pending">
        <div className="ss-msg-row"><span className="ac-bar ac-send-bar" /></div>
      </div>
    </div>
  );
}


function Row({
  person,
  open,
  manageOpen,
  confirming,
  pending,
  busy,
  origin,
  draft,
  onToggle,
  onManage,
  onConfirm,
  onAction,
}: {
  person: AccessPerson;
  open: boolean;
  manageOpen: boolean;
  confirming: AccessAction | null;
  pending: AccessAction | null;
  busy: boolean;
  origin: string;
  draft?: (person: AccessPerson, url: string, registered: boolean) => string;
  onToggle: () => void;
  onManage: () => void;
  onConfirm: (action: AccessAction | null) => void;
  onAction: (action: AccessAction) => void;
}) {
  const { state } = person;
  const firstName = person.name.trim().split(/\s+/)[0] ?? person.name;
  const instant = subLineInstant(state, person.token || person.issuedAt
    ? {
        token: person.token ?? '',
        issuedAt: person.issuedAt ?? '',
        expiresAt: person.expiresAt ?? '',
        usedAt: person.usedAt,
        usedByEmail: person.usedByEmail,
        revokedAt: person.revokedAt,
      }
    : null);
  const sub = accessSubLine(state, { dateLabel: accessDateLabel(instant) });
  const reason = deadLinkReason(state, accessDayLabel(state === 'revoked' ? person.revokedAt : person.expiresAt));
  // WHAT THE LINK IN THE MESSAGE IS, and it is two different things. Before an
  // account exists it is this person's registration token; once they are
  // registered there is no token left and the link is the app itself, which is
  // where Patricia is pointing them when she says "here is where you book".
  const registered = isRegistered(state);
  const url = registered ? origin || '/' : person.token ? `${origin}${joinPath(person.token)}` : null;
  const actions = ACCESS_ACTIONS[state];

  // ONLY THE TWO ACTIONS THAT END IN A LINK get the pending draft. A revoke is
  // not making anything, so drawing the shape of a draft during one would
  // promise the opposite of what is about to happen.
  const issuing = pending === 'new' || pending === 'reset';

  // A DEAD ROW OPENS ITS OWN DRAWER. New link is the only way forward from
  // expired or revoked, so hiding it behind a disclosure would hide the fix.
  const showDrawer =
    !issuing && (manageOpen || confirming !== null || state === 'expired' || state === 'revoked');

  return (
    <div className={'ac-row' + (open || issuing ? ' open' : '')}>
      <button type="button" className="ac-hd" onClick={onToggle} aria-expanded={open || issuing}>
        <Avatar name={person.name} id={`c${person.contactId}`} size={32} />
        {/* THE NAME HAS THE FIRST LINE TO ITSELF (Freddi, 2026-10-12: "it expects and accommodates
            full names … so it fits the space perfectly"). Beside it only the avatar and the
            chevron; the state — what Patricia scans for — leads the line under it, in its colour
            and with its glyph, and the fact about it follows. The full name rides as the title for
            the rare one longer than the row. */}
        <span className="ac-who">
          <b className="ac-name" title={person.name}>{person.name}</b>
          <span className="ac-meta">
            {/* THE BADGE SAYS WHAT IS HAPPENING, NOT WHAT HAS. Showing *Link
                generated* while the request is still in the air would be the one
                claim this column exists to avoid making. */}
            {issuing ? (
              <span className="ac-badge working">
                <span className="ac-ico working">
                  <AccessGlyph state="part" />
                </span>
                <span className="ac-state">Making a link…</span>
              </span>
            ) : (
              <span className={'ac-badge ' + state}>
                <span className={'ac-ico ' + state}>
                  <AccessGlyph state={state} />
                </span>
                <span className="ac-state">{ACCESS_WORD[state]}</span>
              </span>
            )}
            <small className="ac-sub">{sub}</small>
          </span>
        </span>
        <span className="ac-chev">
          <Icon name="chevron-down" size={14} />
        </span>
      </button>

      <DisclosureRegion open={open || issuing}>
        <div className="ac-more">
          {/* AC12 — the refusal sits ABOVE the draft, because it is the newest
              thing about this person and it is why the link they were given did
              not work. The state above it is untouched. */}
          {person.refusedReason && (
            <p className="ac-refused">
              <Icon name="close" size={13} />
              <span>
                Their sign-in was refused — {person.refusedReason}. Give them a ring; there is no way
                to force it through, and that is the point.
              </span>
            </p>
          )}

          {issuing ? (
            <PendingDraft />
          ) : state === 'none' ? (
            <DraftPreview person={person} busy={busy} onGenerate={() => onAction('new')} />
          ) : isCopyable(state) && url ? (
            // THE CARDS' MESSAGE SLOT (Freddi, 2026-10-14: "remove the copy button on the message above
            // and replace with whatsapp button so its inline with how messages look in money and
            // vaccinations cards", and the link on its own goes): Message for them, the words folded,
            // WhatsApp's square with the words in it. The link is in the words.
            <div className="ac-msg ac-draft">
              <MessageSlot fixed first={firstName} phone={person.phone} text={draft ? draft(person, url, registered) : inviteDraftMessage({ name: person.name, url, registered })} />
            </div>
          ) : (
            // ONLY A DEAD LINK IS DRAWN AS ONE. `expired` and `revoked` earn the
            // alert treatment because something the desk sent has stopped
            // working; `none` and `live` are perfectly ordinary states and were
            // wearing the same terracotta box, so a household saved thirty
            // seconds ago read as a fault (Freddi, 2026-09-12).
            <div className={'ac-msg ac-blocked' + (reason ? ' dead' : ' quiet')}>
              <div className="ac-f">
                <div className="ac-f-val ac-reason">
                  {reason ??
                    'Signed in and using the app. There is no link to send.'}
                </div>
              </div>
            </div>
          )}

          {/* MANAGE ACCESS STAYS ON SCREEN WHILE THE LINK IS BEING MADE, simply
              inert. Removing it took 42px out of the card and put them back a
              moment later, which is a jump arriving at the same instant as the
              draft — the two together are what made the swap feel clunky. */}
          <button
            type="button"
            className={'ac-manage' + (showDrawer ? ' on' : '')}
            aria-expanded={showDrawer}
            disabled={issuing}
            onClick={() => {
              onConfirm(null);
              onManage();
            }}
          >
            <ManageIcon />
            <span>Manage access</span>
            <Icon name="chevron-down" size={14} />
          </button>

          <DisclosureRegion open={showDrawer}>
            <div className="ac-drawer">
              {confirming ? (
                // EVERY DESTRUCTIVE ACTION CONFIRMS, INLINE (AC8, AC5b). New link
                // kills an outstanding link and cuts off anyone part-way through;
                // Reset breaks a binding; Revoke ends access.
                <div className="ac-confirm">
                  <p className="ac-cmsg">{confirmMessage(confirming, firstName)}</p>
                  <div className="ac-cacts">
                    <button type="button" className="ac-btn sm" onClick={() => onConfirm(null)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ac-btn sm danger ac-clabel"
                      disabled={busy}
                      onClick={() => {
                        onConfirm(null);
                        onAction(confirming);
                      }}
                    >
                      {CONFIRM_LABEL[confirming]}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ac-danger">
                  {actions.map((action) => (
                    <button
                      key={action}
                      type="button"
                      className={'ac-btn sm ac-a-' + action + (action === 'revoke' ? ' danger' : '')}
                      disabled={busy}
                      onClick={() => {
                        // A FIRST LINK ASKS NOTHING. There is nothing to kill,
                        // so the confirmation would be a question about nothing —
                        // and AC8 confirms *New link where one already exists*.
                        if (action === 'new' && state === 'none') onAction(action);
                        else onConfirm(action);
                      }}
                    >
                      {action === 'revoke' ? <RevokeIcon /> : <ReissueIcon />}
                      {ACCESS_ACTION_LABEL[action]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </DisclosureRegion>
        </div>
      </DisclosureRegion>
    </div>
  );
}

export function AccessColumn({
  people,
  openId,
  manageId,
  confirm,
  pending,
  onToggle,
  onManage,
  onConfirm,
  onAction,
  busy,
  origin,
  draft,
  lead,
  leadHeads = false,
  frame,
  startOpen = false,
}: AccessColumnProps) {
  // CLOSED WHEN THE MODAL OPENS (Freddi, 2026-10-10: "the app access card
  // should be closed by default"); the glance names everyone and how many can
  // sign in. It opens by itself while she is working in a row — a link being
  // made or a row she opened — so a press never vanishes into a shut fold.
  const working = openId !== null || pending !== null;
  const [folded, setFolded] = useState(!startOpen);
  const loading = people === null;
  const list = people ?? [];
  const open = list.length === 0 || working || !folded;
  const tag = accessFoldTag(list);
  // ONE TREE FOR LOADING AND LOADED (2026-10-11): the fold is busy while the
  // people are on their way, in the same place it will stand when they land —
  // a different tree here remounted the request card around it and shut it.
  const rowsEl = (
    <>
            {list.length === 0 ? (
              <div className="ac-empty">Save the household and its people appear here.</div>
            ) : (
              <div className="ac-list">
                {list.map((person) => (
                  <Row
                    key={person.contactId}
                    person={person}
                    open={openId === person.contactId}
                    manageOpen={manageId === person.contactId}
                    confirming={confirm?.contactId === person.contactId ? confirm.action : null}
                    pending={pending?.contactId === person.contactId ? pending.action : null}
                    busy={busy === person.contactId}
                    origin={origin}
                    draft={draft}
                    onToggle={() => onToggle(person.contactId)}
                    onManage={() => onManage(person.contactId)}
                    onConfirm={(action) => onConfirm(action ? { contactId: person.contactId, action } : null)}
                    onAction={(action) => onAction(person, action)}
                  />
                ))}
              </div>
            )}
    </>
  );
  const foldEl = loading ? (
      <div className={'ss-fold access' + (frame ? ' inset' : '')} aria-busy="true"><Skeleton variant="line" /></div>
  ) : (
      <div className={'ss-fold access' + (frame ? ' inset' : '') + (open ? ' open' : '')}>
        {list.length === 0 ? (
          <div className="ss-hd still">
            <span className="ss-ic access"><Icon name="user" size={15} /></span>
            <span className="ss-hdt"><span className="ss-hdrow"><span className="ss-title">App access</span></span></span>
          </div>
        ) : (
        <button type="button" className="ss-hd" aria-expanded={open} onClick={() => setFolded(open)}>
          <span className="ss-ic access"><Icon name="user" size={15} /></span>
          <span className="ss-hdt">
            <span className="ss-hdrow">
              <span className="ss-title">App access</span>
              <span className={'st' + (tag.tone ? ' ' + tag.tone : '')}>{tag.word}</span>
            </span>
            <span className="ss-glance">{accessFoldGlance(list)}</span>
          </span>
          <Icon name="chevron-down" size={16} className="ss-chev" />
        </button>
        )}
        <DisclosureRegion open={open}>
          <div className="ss-body">
            {rowsEl}
            <p className="cp-p mute ac-note">{NOTE}</p>
          </div>
        </DisclosureRegion>
      </div>
  );
  return (
    <aside className="ob-access">
      {/* SELF-SERVICE (Freddi, 2026-10-10): the column is what the customer
          did and what it needs from us — the request first, then who can
          sign in. Was "App access", when App access was all it held. */}
      {!leadHeads && !frame && (
        <div className="ob-pane-head">
          <div>
            <div className="ob-title">Self-service</div>
            <div className="ob-sub">What the customer did, and what it needs from us</div>
          </div>
        </div>
      )}
      {!frame && lead}
      {frame ? frame(loading ? <PeopleSkeleton /> : rowsEl) : foldEl}
    </aside>
  );
}
